import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export const DEFAULT_LAUNCHER_BOTTOM = 20;
const FOOTER_GAP = 20;
const FOOTER_ID = "footer";
const MAIN_CONTENT_SELECTOR = "main";

export function calcLauncherBottom(
  footerTop: number,
  viewportHeight: number = window.innerHeight
): number {
  if (footerTop >= viewportHeight) {
    return DEFAULT_LAUNCHER_BOTTOM;
  }
  return viewportHeight - footerTop + FOOTER_GAP;
}

function getLauncherBottomFromDom(): number {
  const footer = document.getElementById(FOOTER_ID);
  if (!footer) {
    return DEFAULT_LAUNCHER_BOTTOM;
  }

  const footerTop = footer.getBoundingClientRect().top;
  const viewportHeight = window.innerHeight;

  return calcLauncherBottom(footerTop, viewportHeight);
}

export { openChannelMessenger, useChannelTalkMessengerOpen } from "contexts/ChannelTalkContext";

export function useChannelTalkLauncherPosition(): number {
  const [bottom, setBottom] = useState(DEFAULT_LAUNCHER_BOTTOM);
  const rafIdRef = useRef(0);
  const layoutPendingRef = useRef(false);
  const { pathname, search } = useLocation();

  useEffect(() => {
    setBottom(DEFAULT_LAUNCHER_BOTTOM);
    layoutPendingRef.current = true;

    const sync = () => {
      if (layoutPendingRef.current) {
        return;
      }
      setBottom(getLauncherBottomFromDom());
    };

    const scheduleSync = () => {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(sync);
    };

    const enableSyncAndSchedule = () => {
      layoutPendingRef.current = false;
      scheduleSync();
    };

    const scheduleSyncAfterLayout = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(enableSyncAndSchedule);
      });
    };

    window.addEventListener("scroll", scheduleSync, { passive: true });
    window.addEventListener("resize", scheduleSync);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleSync);
      const footer = document.getElementById(FOOTER_ID);
      const main = document.querySelector(MAIN_CONTENT_SELECTOR);

      if (footer) {
        resizeObserver.observe(footer);
      }
      if (main) {
        resizeObserver.observe(main);
      }
    }

    scheduleSyncAfterLayout();

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener("scroll", scheduleSync);
      window.removeEventListener("resize", scheduleSync);
      resizeObserver?.disconnect();
    };
  }, [pathname, search]);

  return bottom;
}
